import {
    Box,
    Heading,
    Input,
    Button,
    Spinner,
    Text,
    Flex,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";

import { fetchAllParts } from "../Api/parts.service";
import { searchPartsByVin } from "../Api/vinSearch.service";

import PartsGrid from "../components/PartsGrid";
import PartsFilters, {
    PartsFilterState,
} from "../components/PartsFilters";

import { applyPartFilters } from "../utils/applyPartFilters";
import { Part } from "../Types/part";
import { useVehicle } from "../../contexts/VehicleContext";

const PartsExplorerPage = () => {
    /* ============================
       GLOBAL FILTER STATE (SHARED)
       ============================ */
    const [filters, setFilters] = useState<PartsFilterState>({
        oem: "all",
        inStockOnly: false,
    });

    /* ============================
       ALL PARTS STATE
       ============================ */
    const [allParts, setAllParts] = useState<Part[]>([]);
    const [allLoading, setAllLoading] = useState(true);
    const [allError, setAllError] = useState("");

    /* ============================
       VIN SEARCH STATE
       ============================ */
    const [vin, setVin] = useState("");
    const [vinLoading, setVinLoading] = useState(false);
    const [vinError, setVinError] = useState("");
    const [vinParts, setVinParts] = useState<Part[]>([]);

    const { vehicle, setVehicle } = useVehicle();

    /* ============================
       LOAD ALL PARTS (ONCE)
       ============================ */
    useEffect(() => {
        const loadParts = async () => {
            try {
                const data = await fetchAllParts();
                setAllParts(data);
            } catch {
                setAllError("Failed to load parts catalog.");
            } finally {
                setAllLoading(false);
            }
        };

        loadParts();
    }, []);

    /* ============================
       VIN SEARCH HANDLER
       ============================ */
    const handleVinSearch = async () => {
        if (!vin) return;

        try {
            setVinLoading(true);
            setVinError("");
            setVinParts([]);

            const result = await searchPartsByVin(vin.trim().toUpperCase());

            setVehicle(result.vehicle);
            setVinParts(result.parts);
        } catch {
            setVinError("Invalid VIN or no compatible parts found.");
            setVinParts([]);
        } finally {
            setVinLoading(false);
        }
    };

    /* ============================
       FILTERED RESULTS
       ============================ */
    const filteredAllParts = useMemo(
        () => applyPartFilters(allParts, filters),
        [allParts, filters]
    );

    const filteredVinParts = useMemo(
        () => applyPartFilters(vinParts, filters),
        [vinParts, filters]
    );

    /* ============================
       RENDER
       ============================ */
    return (
        <Box p={4}>
            <Tabs variant="enclosed-colored"
                colorScheme="teal" defaultIndex={0} mb={2}>
                <TabList>
                    <Tab fontWeight="bold" color={'rgb(105, 140, 165)'}
                        _selected={{
                            color: 'gray.600',
                            bg: '#fff',
                            borderTop: '3px solid teal',
                        }}>Search by VIN</Tab>
                    <Tab fontWeight="bold" color={'rgb(105, 140, 165)'}
                        _selected={{
                            color: 'gray.600',
                            bg: '#fff',
                            borderTop: '3px solid teal',
                        }}>All Parts</Tab>
                </TabList>

                <TabPanels>
                    {/* =======================
              VIN SEARCH TAB
             ======================= */}
                    <TabPanel px={0}>
                        <Flex gap={3} maxW="500px" mb={4}>
                            <Input
                                border={'1px solid #a3a3a3ff'}
                                _placeholder={{ fontWeight: '500', color: 'gray.400' }}
                                color="gray.700"
                                _hover={{ borderColor: 'teal.400' }}
                                _focus={{ borderColor: 'teal.500', boxShadow: '0 0 0 0px teal' }}
                                name="seatsAvailable"
                                placeholder="Enter VIN"
                                value={vin}
                                onChange={(e) => setVin(e.target.value)}
                            />
                            <Button colorScheme="teal" onClick={handleVinSearch}>
                                Search
                            </Button>
                        </Flex>

                        {vinLoading && <Spinner mt={4} />}

                        {vinError && (
                            <Text mt={4} color="red.500">
                                {vinError}
                            </Text>
                        )}

                        {vehicle && (
                            <Box bg={'#fff'} mt={4} p={4} borderWidth="1px" borderRadius="md">
                                <Text fontWeight="bold">
                                    {vehicle.make} {vehicle.model} ({vehicle.year})
                                </Text>
                                <Text fontSize="sm">Engine: {vehicle.engine}</Text>
                            </Box>
                        )}

                        {vinParts.length > 0 && (
                            <Box mt={6}>
                                <PartsFilters filters={filters} onChange={setFilters} />
                                <PartsGrid parts={filteredVinParts} />
                            </Box>
                        )}

                        {!vinLoading && vinParts.length === 0 && vehicle && (
                            <Text mt={4}>No compatible parts found.</Text>
                        )}
                    </TabPanel>

                    {/* =======================
              ALL PARTS TAB
             ======================= */}
                    <TabPanel px={0}>
                        <PartsFilters filters={filters} onChange={setFilters} />

                        {allLoading && <Spinner mt={4} />}

                        {allError && (
                            <Text mt={4} color="red.500">
                                {allError}
                            </Text>
                        )}

                        {!allLoading && !allError && (
                            <PartsGrid parts={filteredAllParts} />
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
};

export default PartsExplorerPage;
